import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FlowRolesManager } from "@/components/FlowRolesManager";
import { Loader2, Plus, Trash2, Pencil, Workflow, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, X, Copy, ArrowLeft, ArrowRight, Lock, Clock, User, ListTree, Maximize2, Minimize2, FileText, Share2 } from "lucide-react";

export default function ConsoleTaskFlows({ embedded = false, isSuper = false }) {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const openEditor = (id) => navigate(`/console/task-flows/${id}`);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/agency/task-flows");
    setFlows(data.flows || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (flow) => {
    if (!(await confirmDelete({ message: `Delete the “${flow.name}” flow? It will be removed from any catalogue items using it.`, confirmText: "Delete" }))) return;
    try { await api.delete(`/agency/task-flows/${flow.id}`); toast.success("Flow deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dup = async (flow) => {
    try { await api.post(`/agency/task-flows/${flow.id}/duplicate`); toast.success("Flow duplicated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className={embedded ? "" : "px-6 md:px-12 py-12 max-w-4xl mx-auto"} data-testid="task-flow-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          {!embedded && <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Task Flow</h1>}
          <p className={`text-sm text-[#9FA1A4] max-w-xl ${embedded ? "" : "mt-2"}`}>Reusable task sequences you can import into any project or attach to catalogue items. Set who each task goes to (a person, a Flow Role, or the scheduler) and when it's due relative to the start.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="content-flow-new" size="sm" variant="outline" onClick={() => navigate(`/console/task-flows/${newKey()}?kind=content_doc`)} className="border-[#C9B8FF] text-[#5b2fd6] rounded-full"><FileText className="h-4 w-4 mr-1.5" /> New content flow</Button>
          <Button data-testid="social-flow-new" size="sm" variant="outline" onClick={() => navigate(`/console/task-flows/${newKey()}?kind=social_posts`)} className="border-[#C9B8FF] text-[#5b2fd6] rounded-full"><Share2 className="h-4 w-4 mr-1.5" /> New social flow</Button>
          <Button data-testid="task-flow-new" size="sm" onClick={() => openEditor(newKey())} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New flow</Button>
        </div>
      </div>

      <FlowRolesManager canEdit={isSuper} />

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>
      ) : flows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] p-12 text-center">
          <div className="h-12 w-12 mx-auto rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center mb-3"><Workflow className="h-5 w-5" /></div>
          <p className="text-sm text-[#9FA1A4]">No task flows yet. Create one to reuse across projects.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="task-flow-list">
          {flows.map((f) => (
            <div key={f.id} data-testid="task-flow-row" className="flex items-center gap-3 rounded-2xl border border-[#EEEFEF] bg-white px-4 py-3">
              {f.kind === "content_doc" || f.kind === "social_posts" ? <span className="h-9 w-9 rounded-xl bg-[#06060C] text-white flex items-center justify-center shrink-0">{f.kind === "social_posts" ? <Share2 className="h-4 w-4" /> : <FileText className="h-4 w-4" />}</span>
                : <span className="h-9 w-9 rounded-xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0"><Workflow className="h-4 w-4" /></span>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#06060C] truncate flex items-center gap-2">{f.name}{f.kind && <span data-testid={`content-flow-badge-${f.id}`} className="font-mono-data text-[9px] uppercase tracking-wider rounded-full bg-[#06060C] text-white px-1.5 py-0.5">{f.kind === "social_posts" ? "Social · SocialStudio" : "Content · Docs"}</span>}</p>
                <p className="text-xs text-[#9FA1A4] truncate">{f.kind === "content_doc" ? "One main task with draft, review, rework, publish and verify subtasks" : f.kind === "social_posts" ? "One main task with draft, review, client approval, revise and schedule subtasks" : `${(f.tasks || []).length} task${(f.tasks || []).length === 1 ? "" : "s"}`}{f.description ? ` · ${f.description}` : ""}</p>
              </div>
              <button data-testid={`task-flow-edit-${f.id}`} onClick={() => openEditor(f.id)} title="Edit" className="text-[#c4c5cc] hover:text-[#723FFF]"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`task-flow-duplicate-${f.id}`} onClick={() => dup(f)} title="Duplicate" className="text-[#c4c5cc] hover:text-[#723FFF]"><Copy className="h-4 w-4" /></button>
              <button data-testid={`task-flow-delete-${f.id}`} onClick={() => del(f)} title="Delete" className="text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `k${Date.now()}${Math.random().toString(36).slice(2)}`);

function blankTask(statuses) {
  return { key: newKey(), description: "", depends_on_key: null, title: "", status: null, due_offset_days: null, due_basis: "project_start", time_estimate_minutes: 0, assign_type: "scheduler", assign_user_id: null, assign_position: null, subtasks: [] };
}
